import { EnvironmentInjector, NgModule, inject, runInInjectionContext } from '@angular/core';
import { CanActivateChildFn, PreloadAllModules, RouterModule, Routes } from '@angular/router';
import { angularFireAuthGuard, redirectUnauthorizedTo, redirectLoggedInTo } from './app-auth-guard';
import { CHAT_PRODUCT_ID, defaultProductHome } from './config/product';
import { productAccessGuard } from './product-access.guard';
import { connectFeatureGuard } from './connect-feature.guard';
import { nativeShellOnlyGuard } from './native/native-shell-only.guard';
import { notInMobileAppGuard } from './not-in-mobile-app.guard';

// Lazy wrapper around the chat surface's context guard: a static import here
// would pull the chat org-context/data-store/firebase graph into the INITIAL
// bundle even though every chat route is lazy.
const lazyChatContextGuard: CanActivateChildFn = () => {
  const injector = inject(EnvironmentInjector);
  return import('./chat/chat-context.guard').then((m) =>
    runInInjectionContext(injector, () => m.chatContextGuard()));
};

const redirectUnauthorizedToLogin = () => redirectUnauthorizedTo(['login']);
// Hostname-aware: chat.* (dual-served Chat front door) lands on /chat.
const redirectLoggedInToHome = () => redirectLoggedInTo([defaultProductHome()]);

export const routes: Routes = [
  {
    path: 'product-disabled',
    title: 'Product Disabled',
    loadChildren: () => import('./pages/product-disabled/product-disabled.module').then( m => m.ProductDisabledPageModule),
    // Auth only — no productAccessGuard (this page IS the denial target; the
    // product guard would loop). Unauthenticated visitors must never see the
    // workspace chrome this page deliberately renders.
    canActivate: [angularFireAuthGuard], data: { authGuardPipe: redirectUnauthorizedToLogin },
  },
  {
    // Data residency: the active org's region cannot be connected (no project
    // here yet / not live / session refused). Auth only — like
    // product-disabled, this page IS the denial target.
    path: 'region-unavailable',
    title: 'Region unavailable',
    loadComponent: () => import('./pages/region-unavailable/region-unavailable.page').then((m) => m.RegionUnavailablePage),
    canActivate: [angularFireAuthGuard], data: { authGuardPipe: redirectUnauthorizedToLogin },
  },
  {
    path: 'account',
    title: 'Account',
    loadChildren: () => import('./pages/account-settings/account-settings.module').then(m => m.AccountSettingsPageModule),
    canActivate: [angularFireAuthGuard], data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'messaging',
    title: 'Messaging',
    loadChildren: () => import('./pages/messaging/messaging.module').then( m => m.MessagingPageModule),
    canActivate: [angularFireAuthGuard, productAccessGuard], data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    // Agents base page: "Start from scratch", then the marketplace's agents
    // only. Declared before `agents` so AgentsPage's '' child can't swallow it.
    path: 'agents/start',
    title: 'Agents',
    loadComponent: () => import('./pages/section-start/section-start.page').then((m) => m.SectionStartPage),
    canActivate: [angularFireAuthGuard, productAccessGuard, connectFeatureGuard],
    data: { authGuardPipe: redirectUnauthorizedToLogin, features: ['agents'], kind: 'agent' }
  },
  {
    // Teams base page: same layout, the marketplace's teams only.
    path: 'projects/start',
    title: 'Projects',
    loadComponent: () => import('./pages/section-start/section-start.page').then((m) => m.SectionStartPage),
    canActivate: [angularFireAuthGuard, productAccessGuard, connectFeatureGuard],
    data: { authGuardPipe: redirectUnauthorizedToLogin, features: ['teams'], kind: 'team' }
  },
  {
    // The catalog view (scratch + ready-made) even when the user has agents —
    // where the rail's "New agent" row and the roster banner go.
    path: 'agents/hire',
    title: 'New agent',
    loadComponent: () => import('./pages/section-start/section-start.page').then((m) => m.SectionStartPage),
    canActivate: [angularFireAuthGuard, productAccessGuard, connectFeatureGuard],
    data: { authGuardPipe: redirectUnauthorizedToLogin, features: ['agents'], kind: 'agent', view: 'new' }
  },
  {
    path: 'projects/hire',
    title: 'New project',
    loadComponent: () => import('./pages/section-start/section-start.page').then((m) => m.SectionStartPage),
    canActivate: [angularFireAuthGuard, productAccessGuard, connectFeatureGuard],
    data: { authGuardPipe: redirectUnauthorizedToLogin, features: ['teams'], kind: 'team', view: 'new' }
  },
  {
    // Assemble a team from scratch — a page in the hire wizard's frame (was a modal).
    path: 'projects/new',
    title: 'Start a project',
    loadComponent: () => import('./pages/create-team/create-team.page').then((m) => m.CreateTeamPage),
    canActivate: [angularFireAuthGuard, productAccessGuard, connectFeatureGuard],
    data: { authGuardPipe: redirectUnauthorizedToLogin, features: ['teams'] }
  },
  { path: 'projects', redirectTo: 'projects/start', pathMatch: 'full' },
  // Teams became Projects (2026-10-21): old links and bookmarks keep working.
  { path: 'teams', redirectTo: 'projects/start', pathMatch: 'full' },
  { path: 'teams/start', redirectTo: 'projects/start' },
  { path: 'teams/hire', redirectTo: 'projects/hire' },
  { path: 'teams/new', redirectTo: 'projects/new' },
  {
    path: 'agents/new',
    title: 'Create Agent',
    loadChildren: () => import('./pages/create-agent/create-agent.module').then( m => m.CreateAgentPageModule),
    canActivate: [angularFireAuthGuard, productAccessGuard, connectFeatureGuard],
    data: { authGuardPipe: redirectUnauthorizedToLogin, features: ['agents'] }
  },
  {
    path: 'agents',
    title: 'Agents',
    loadChildren: () => import('./pages/agents/agents.module').then( m => m.AgentsPageModule),
    canActivate: [angularFireAuthGuard, productAccessGuard], data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'tasks',
    title: 'Tasks',
    loadChildren: () => import('./pages/tasks/tasks.module').then( m => m.TasksPageModule),
    canActivate: [angularFireAuthGuard, productAccessGuard], data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    // User + org memory (MEMORY.md "Connect"): /o/{orgId}/memory in the address
    // bar (OrgLocationStrategy). ?tab=mine|org|review, ?note=&layer= deep links.
    path: 'memory',
    title: 'Memory',
    loadComponent: () => import('./pages/memory/memory.page').then((m) => m.MemoryPage),
    canActivate: [angularFireAuthGuard, productAccessGuard], data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    // Web "Local models": the desktop app download page (the shell opens its model manager instead).
    path: 'local-models',
    title: 'Local models',
    loadComponent: () => import('./pages/local-models/local-models.page').then((m) => m.LocalModelsPage),
    // Not in the mobile app: this is the DESKTOP app's download page (notInMobileAppGuard).
    canActivate: [notInMobileAppGuard, angularFireAuthGuard],
    data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    // The full marketplace (agents AND teams; crewdle.com's `?hire=<slug>`
    // deep links). The Agents / Teams base pages embed a filtered copy.
    path: 'marketplace',
    title: 'Marketplace',
    loadComponent: () => import('./pages/marketplace/marketplace.page').then((m) => m.MarketplacePage),
    canActivate: [angularFireAuthGuard, productAccessGuard, connectFeatureGuard],
    data: { authGuardPipe: redirectUnauthorizedToLogin, features: ['agents', 'teams'] }
  },
  {
    path: 'messaging/conversation/:id',
    title: 'Conversation',
    loadChildren: () => import('./pages/conversation/conversation.module').then( m => m.ConversationPageModule),
    canActivate: [angularFireAuthGuard, productAccessGuard], data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'messaging/contacts/:contact-type/:id',
    title: 'Contact',
    loadChildren: () => import('./pages/contact-details/contact-details.module').then( m => m.ContactDetailsModule),
    canActivate: [angularFireAuthGuard, productAccessGuard], data: { authGuardPipe: redirectUnauthorizedToLogin }
  },
  {
    path: 'support',
    title: 'Support',
    loadChildren: () => import('./pages/support/support.module').then( m => m.SupportPageModule)
  },
  {
    path: 'support/:errorType',
    title: 'Support',
    loadChildren: () => import('./pages/support/support.module').then( m => m.SupportPageModule)
  },
  {
    // The four native/ routes below match ONLY inside the mobile shell (nativeShellOnlyGuard): on the web they
    // do not exist. In-app login for native shells (no auth guard: this page IS the login; it
    // redirects signed-in users itself). Must precede login/:lang.
    path: 'login/native',
    title: 'Login',
    loadComponent: () => import('./native/auth/native-login.page').then((m) => m.NativeLoginPage),
    canMatch: [nativeShellOnlyGuard],
  },
  {
    // In-app sign-up for native shells (twin of the portal's /signup); like login/native, no guard.
    path: 'login/native/signup',
    title: 'Sign up',
    loadComponent: () => import('./native/auth/native-signup.page').then((m) => m.NativeSignupPage),
    canMatch: [nativeShellOnlyGuard],
  },
  {
    // Firebase e-mail links (verify e-mail, reset password): the shell routes https://auth.crewdle.ai/__/auth?...
    // here so those flows finish in the app. No guard: the page applies a one-time code, and needs no session.
    path: 'email-action',
    title: 'E-mail action',
    loadComponent: () => import('./native/auth/native-email-action.page').then((m) => m.NativeEmailActionPage),
    canMatch: [nativeShellOnlyGuard],
  },
  {
    // In-app onboarding for native shells (twin of the portal's /onboarding). No guard for the same
    // reason as login/native; the page sends away anyone who is not a signed-in account that needs it.
    path: 'onboarding/native',
    title: 'Onboarding',
    loadComponent: () => import('./native/onboarding/native-onboarding.page').then((m) => m.NativeOnboardingPage),
    canMatch: [nativeShellOnlyGuard],
  },
  {
    path: 'login',
    title: 'Login',
    loadComponent: () => import('./pages/auth-handoff/auth-handoff.component').then( m => m.AuthHandoffComponent),
    canActivate: [angularFireAuthGuard], data: { authGuardPipe: redirectLoggedInToHome }
  },
  {
    path: 'login/:lang',
    title: 'Login',
    loadComponent: () => import('./pages/auth-handoff/auth-handoff.component').then( m => m.AuthHandoffComponent),
    canActivate: [angularFireAuthGuard], data: { authGuardPipe: redirectLoggedInToHome }
  },
  {
    path: 'upgrade',
    title: 'Upgrade',
    loadChildren: () => import('./pages/upgrade/upgrade.module').then( m => m.UpgradePageModule)
  },
  {
    // Public viewer for shared agent-built apps (no guard: recipients are
    // external and unauthenticated — the unguessable share id is the access).
    path: 'shared/apps/:id',
    title: 'Shared App',
    loadComponent: () => import('./pages/shared-app/shared-app.page').then((m) => m.SharedAppPage),
  },
  {
    // Neutral interstitial during global sign-out (no guard: shown while the
    // session is being torn down). See SigningOutComponent.
    path: 'signing-out',
    title: 'Signing out',
    loadComponent: () => import('./pages/signing-out/signing-out.component').then((m) => m.SigningOutComponent),
  },
  {
    // ── Hosted Chat product surface (src/app/chat, lifted from chat-ui). ──
    // Chat is a separately-sellable product: productAccessGuard gates it on
    // CHAT_PRODUCT_ID (not connect's own product id); chatContextGuard boots
    // the chat org context (users/{uid} → active org) before children run.
    path: 'chat',
    title: 'Chat',
    canActivate: [angularFireAuthGuard, productAccessGuard],
    canActivateChild: [lazyChatContextGuard],
    data: { authGuardPipe: redirectUnauthorizedToLogin, productId: CHAT_PRODUCT_ID },
    loadComponent: () => import('./chat/layout/layout.component').then((m) => m.LayoutComponent),
    children: [
      { path: '', loadComponent: () => import('./chat/pages/home/home.component').then((m) => m.HomeComponent) },
      { path: 'c/:id', loadComponent: () => import('./chat/pages/chat/chat.component').then((m) => m.ChatComponent) },
      // Topics (stored as "folders": ids, data and i18n keys keep that name).
      { path: 'topics', loadComponent: () => import('./chat/pages/topics/topics.component').then((m) => m.TopicsComponent) },
      { path: 'topics/:id', loadComponent: () => import('./chat/pages/folder/folder.component').then((m) => m.FolderPageComponent) },
      // Old folder links (bookmarks, shares) keep working; ?owner= is kept.
      { path: 'folders/:id', redirectTo: 'topics/:id', pathMatch: 'full' },
      { path: 'folder/:id', redirectTo: 'topics/:id', pathMatch: 'full' },
    ],
  },
  {
    // Public viewer for shared chats (no guard: recipients are external and
    // unauthenticated — the path contract is exactly /s/:id).
    path: 's/:id',
    title: 'Shared Chat',
    loadComponent: () => import('./chat/pages/shared-chat/shared-chat.component').then((m) => m.SharedChatComponent),
  },
  {
    // Auth-portal bounce targets registered for the chat.* front door
    // (dual-served bundle): resolve a session or hand off to centralized auth.
    path: 'auth-start',
    title: 'Signing in',
    loadComponent: () => import('./chat/pages/auth-start/auth-start.component').then((m) => m.AuthStartComponent),
  },
  {
    path: 'auth-end',
    title: 'Signing in',
    loadComponent: () => import('./chat/pages/auth-end/auth-end.component').then((m) => m.AuthEndComponent),
  },
  {
    // Microsoft Teams tab: shown when Teams SSO can't sign the user in.
    path: 'teams-no-access',
    title: 'Crewdle',
    loadComponent: () => import('./chat/pages/teams-no-access/teams-no-access.component').then((m) => m.TeamsNoAccessComponent),
  },
  {
    // Chat-ui's public error landing (auth-end routes here on a failed code
    // exchange). Kept top-level and unguarded like the original.
    path: 'error',
    title: 'Error',
    loadComponent: () => import('./chat/pages/error/error.component').then((m) => m.ErrorComponent),
  },
  {
    path: '**', redirectTo: '/login', pathMatch: 'full'
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, { preloadingStrategy: PreloadAllModules })
  ],
  exports: [RouterModule]
})
export class AppRoutingModule { }
